
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import { Checkbox } from "@/components/ui/checkbox";
import LogoUpload from "@/components/LogoUpload";

const EditApp = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    seoDescription: "",
    visitUrl: "",
    selectedCategories: [] as string[],
    selectedPlatforms: [] as string[],
    selectedSports: [] as string[],
    logoUrl: "",
    iosStoreUrl: "",
    androidStoreUrl: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  
  // Database data
  const [dbCategories, setDbCategories] = useState<{id: string, name: string}[]>([]);
  const [dbPlatforms, setDbPlatforms] = useState<{id: string, name: string}[]>([]);
  const [dbSports, setDbSports] = useState<{id: string, name: string}[]>([]);

  // Check authentication status when component mounts and set up listener
  useEffect(() => {
    const setupAuth = async () => {
      await checkAuthStatus();
      
      // Set up auth state listener
      const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
        console.log("Auth state changed in EditApp:", event, "Session exists:", !!session);
        if (session) {
          console.log("User ID from auth state change:", session.user.id);
          setUserId(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          navigate('/auth');
        }
      });
      
      return () => {
        authListener.subscription.unsubscribe();
      };
    };
    
    setupAuth();
  }, []);

  // Separate effect to load app details after user ID is set
  useEffect(() => {
    if (userId && id) {
      console.log("User ID set in EditApp, loading app details with user ID:", userId);
      loadAppDetails(id);
    }
  }, [userId, id]);

  const checkAuthStatus = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Redirect to auth page if not authenticated
        console.log("No session found in EditApp, redirecting to auth");
        navigate("/auth");
        return;
      }
      
      console.log("User is authenticated in EditApp, user ID:", session.user.id);
      setUserId(session.user.id);
      await loadData();
    } catch (error) {
      console.error('Error checking auth status:', error);
      toast({
        title: "Error checking authentication",
        description: "There was a problem verifying your login status. Please try logging in again.",
        variant: "destructive",
      });
      navigate("/auth"); // Redirect to auth on error
    } finally {
      setIsLoading(false);
    }
  };

  const loadData = async () => {
    try {
      // Load categories, platforms, and sports from the database
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('id, name')
        .order('name');

      const { data: platformsData, error: platformsError } = await supabase
        .from('platforms')
        .select('id, name')
        .order('name');

      const { data: sportsData, error: sportsError } = await supabase
        .from('sports')
        .select('id, name')
        .order('name');

      if (categoriesError) {
        console.error('Error loading categories:', categoriesError);
        toast({
          title: "Error loading categories",
          description: categoriesError.message,
          variant: "destructive",
        });
      }
      
      if (platformsError) {
        console.error('Error loading platforms:', platformsError);
        toast({
          title: "Error loading platforms",
          description: platformsError.message,
          variant: "destructive",
        });
      }
      
      if (sportsError) {
        console.error('Error loading sports:', sportsError);
        toast({
          title: "Error loading sports",
          description: sportsError.message,
          variant: "destructive",
        });
      }

      if (categoriesData) setDbCategories(categoriesData);
      if (platformsData) setDbPlatforms(platformsData);
      if (sportsData) setDbSports(sportsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error loading data",
        description: "There was an error loading form data. Please try refreshing the page.",
        variant: "destructive",
      });
    }
  };

  const loadAppDetails = async (appId: string) => {
    try {
      console.log("Loading app details for ID:", appId);
      console.log("Current user ID in loadAppDetails:", userId);
      
      if (!userId) {
        console.error("User ID is null, cannot verify ownership");
        toast({
          title: "Authentication error",
          description: "Please try again after ensuring you are logged in.",
          variant: "destructive",
        });
        navigate('/my-listings');
        return;
      }
      
      // First check if this app exists and belongs to the current user
      const { data: appData, error: appError } = await supabase
        .from('apps')
        .select('*')
        .eq('id', appId)
        .maybeSingle();

      if (appError) {
        console.error("Error fetching app:", appError);
        throw appError;
      }

      if (!appData) {
        console.error("App not found");
        toast({
          title: "App not found",
          description: "The app you're trying to edit could not be found.",
          variant: "destructive",
        });
        navigate('/my-listings');
        return;
      }

      // Debug ownership verification
      console.log('App details found:', appData);
      console.log('App user_id:', appData.user_id);
      console.log('Current user ID for comparison:', userId);
      
      // Make sure to verify ownership using the authenticated user's ID
      if (userId !== appData.user_id) {
        console.error("Unauthorized - app belongs to:", appData.user_id, "but current user is:", userId);
        toast({
          title: "Unauthorized",
          description: "You don't have permission to edit this app.",
          variant: "destructive",
        });
        navigate('/my-listings');
        return;
      }

      console.log("Authorization successful, loading relationships");

      // Load app categories
      const { data: categoryData, error: categoryError } = await supabase
        .from('app_categories')
        .select('category_id')
        .eq('app_id', appId);

      if (categoryError) throw categoryError;

      // Load app platforms
      const { data: platformData, error: platformError } = await supabase
        .from('app_platforms')
        .select('platform_id')
        .eq('app_id', appId);

      if (platformError) throw platformError;

      // Load app sports
      const { data: sportData, error: sportError } = await supabase
        .from('app_sports')
        .select('sport_id')
        .eq('app_id', appId);

      if (sportError) throw sportError;

      console.log("Setting form data");
      // Set form data
      setFormData({
        name: appData.name,
        description: appData.description,
        seoDescription: appData.seo_description || "",
        visitUrl: appData.visit_url,
        selectedCategories: categoryData.map(item => item.category_id),
        selectedPlatforms: platformData.map(item => item.platform_id),
        selectedSports: sportData.map(item => item.sport_id),
        logoUrl: appData.logo_url || "",
        iosStoreUrl: appData.ios_store_url || "",
        androidStoreUrl: appData.android_store_url || "",
      });
      
      console.log("Form data set successfully");
    } catch (error: any) {
      console.error('Error loading app details:', error);
      toast({
        title: "Error loading app details",
        description: error.message || "There was an error loading the app details.",
        variant: "destructive",
      });
      navigate('/my-listings');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) return;
    
    // Form validation
    if (!formData.name || !formData.description || !formData.visitUrl) {
      toast({
        title: "Missing required fields",
        description: "Please fill out all required fields.",
        variant: "destructive",
      });
      return;
    }

    if (formData.selectedCategories.length === 0 || 
        formData.selectedPlatforms.length === 0 || 
        formData.selectedSports.length === 0) {
      toast({
        title: "Missing selections",
        description: "Please select at least one category, platform, and sport.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      console.log("Starting app update process...");
      
      // Update the app
      const { error: appError } = await supabase
        .from('apps')
        .update({
          name: formData.name,
          description: formData.description,
          seo_description: formData.seoDescription || formData.description,
          visit_url: formData.visitUrl,
          logo_url: formData.logoUrl,
          ios_store_url: formData.iosStoreUrl,
          android_store_url: formData.androidStoreUrl,
        })
        .eq('id', id);

      if (appError) throw appError;
      
      console.log("App details updated successfully");

      // ===== CATEGORIES UPDATE =====
      console.log("Updating app categories...");
      
      // Get current category relationships
      const { data: currentCategories, error: getCategoriesError } = await supabase
        .from('app_categories')
        .select('category_id')
        .eq('app_id', id);
      
      if (getCategoriesError) throw getCategoriesError;
      
      // Find categories to remove (in current but not in selected)
      const currentCategoryIds = currentCategories.map(c => c.category_id);
      const categoriesToRemove = currentCategoryIds.filter(
        catId => !formData.selectedCategories.includes(catId)
      );
      
      // Find categories to add (in selected but not in current)
      const categoriesToAdd = formData.selectedCategories.filter(
        catId => !currentCategoryIds.includes(catId)
      );
      
      console.log("Categories to remove:", categoriesToRemove);
      console.log("Categories to add:", categoriesToAdd);
      
      // Delete categories that are no longer selected
      if (categoriesToRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('app_categories')
          .delete()
          .eq('app_id', id)
          .in('category_id', categoriesToRemove);
        
        if (deleteError) throw deleteError;
        console.log("Removed categories successfully");
      }
      
      // Add new categories
      if (categoriesToAdd.length > 0) {
        const categoryRelations = categoriesToAdd.map(categoryId => ({
          app_id: id,
          category_id: categoryId
        }));
        
        const { error: addError } = await supabase
          .from('app_categories')
          .insert(categoryRelations);
        
        if (addError) throw addError;
        console.log("Added new categories successfully");
      }
      
      // ===== PLATFORMS UPDATE =====
      console.log("Updating app platforms...");
      
      // Get current platform relationships
      const { data: currentPlatforms, error: getPlatformsError } = await supabase
        .from('app_platforms')
        .select('platform_id')
        .eq('app_id', id);
      
      if (getPlatformsError) throw getPlatformsError;
      
      // Find platforms to remove (in current but not in selected)
      const currentPlatformIds = currentPlatforms.map(p => p.platform_id);
      const platformsToRemove = currentPlatformIds.filter(
        platId => !formData.selectedPlatforms.includes(platId)
      );
      
      // Find platforms to add (in selected but not in current)
      const platformsToAdd = formData.selectedPlatforms.filter(
        platId => !currentPlatformIds.includes(platId)
      );
      
      // Delete platforms that are no longer selected
      if (platformsToRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('app_platforms')
          .delete()
          .eq('app_id', id)
          .in('platform_id', platformsToRemove);
        
        if (deleteError) throw deleteError;
      }
      
      // Add new platforms
      if (platformsToAdd.length > 0) {
        const platformRelations = platformsToAdd.map(platformId => ({
          app_id: id,
          platform_id: platformId
        }));
        
        const { error: addError } = await supabase
          .from('app_platforms')
          .insert(platformRelations);
        
        if (addError) throw addError;
      }
      
      // ===== SPORTS UPDATE =====
      console.log("Updating app sports...");
      
      // Get current sport relationships
      const { data: currentSports, error: getSportsError } = await supabase
        .from('app_sports')
        .select('sport_id')
        .eq('app_id', id);
      
      if (getSportsError) throw getSportsError;
      
      // Find sports to remove (in current but not in selected)
      const currentSportIds = currentSports.map(s => s.sport_id);
      const sportsToRemove = currentSportIds.filter(
        sportId => !formData.selectedSports.includes(sportId)
      );
      
      // Find sports to add (in selected but not in current)
      const sportsToAdd = formData.selectedSports.filter(
        sportId => !currentSportIds.includes(sportId)
      );
      
      // Delete sports that are no longer selected
      if (sportsToRemove.length > 0) {
        const { error: deleteError } = await supabase
          .from('app_sports')
          .delete()
          .eq('app_id', id)
          .in('sport_id', sportsToRemove);
        
        if (deleteError) throw deleteError;
      }
      
      // Add new sports
      if (sportsToAdd.length > 0) {
        const sportRelations = sportsToAdd.map(sportId => ({
          app_id: id,
          sport_id: sportId
        }));
        
        const { error: addError } = await supabase
          .from('app_sports')
          .insert(sportRelations);
        
        if (addError) throw addError;
      }

      toast({
        title: "App updated successfully",
        description: "Your app has been updated.",
      });
      
      navigate("/my-listings");
    } catch (error: any) {
      console.error('Error updating app:', error);
      toast({
        title: "Error updating app",
        description: error?.message || "There was an error updating your app. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoUpload = (url: string) => {
    setFormData(prev => ({
      ...prev,
      logoUrl: url
    }));
  };

  const toggleCategory = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedCategories: prev.selectedCategories.includes(categoryId)
        ? prev.selectedCategories.filter(id => id !== categoryId)
        : [...prev.selectedCategories, categoryId]
    }));
  };

  const togglePlatform = (platformId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedPlatforms: prev.selectedPlatforms.includes(platformId)
        ? prev.selectedPlatforms.filter(id => id !== platformId)
        : [...prev.selectedPlatforms, platformId]
    }));
  };

  const toggleSport = (sportId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedSports: prev.selectedSports.includes(sportId)
        ? prev.selectedSports.filter(id => id !== sportId)
        : [...prev.selectedSports, sportId]
    }));
  };

  if (isLoading) {
    return (
      <div>
        <Navigation />
        <div className="container py-12 flex justify-center items-center">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <div className="container py-8">
        <h1 className="text-2xl font-bold mb-6">Edit App</h1>
        <div className="max-w-2xl mx-auto bg-white rounded-lg border p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <label htmlFor="name">App Name</label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div className="grid gap-2">
                <label htmlFor="description">
                  Description <span className="text-xs text-gray-500">(max 500 characters)</span>
                </label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  maxLength={500}
                  className="min-h-[120px]"
                  required
                />
                <div className="text-xs text-gray-500 text-right">
                  {formData.description.length}/500 characters
                </div>
              </div>

              <div className="grid gap-2">
                <label htmlFor="visitUrl">Website URL</label>
                <Input
                  id="visitUrl"
                  type="url"
                  value={formData.visitUrl}
                  onChange={(e) => setFormData(prev => ({ ...prev, visitUrl: e.target.value }))}
                  required
                />
              </div>

              <div className="grid gap-2">
                <label>App Logo</label>
                {formData.logoUrl && (
                  <div className="mb-2">
                    <img 
                      src={formData.logoUrl} 
                      alt="App Logo" 
                      className="w-20 h-20 object-contain bg-gray-50 rounded-md"
                    />
                  </div>
                )}
                <LogoUpload onLogoUpload={handleLogoUpload} />
              </div>

              <div className="grid gap-2">
                <label className="font-medium">Categories</label>
                <div className="grid grid-cols-2 gap-2">
                  {dbCategories.map((category) => (
                    <div key={category.id} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`category-${category.id}`} 
                        checked={formData.selectedCategories.includes(category.id)}
                        onCheckedChange={() => toggleCategory(category.id)}
                      />
                      <label 
                        htmlFor={`category-${category.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {category.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-2">
                <label className="font-medium">Platforms</label>
                <div className="grid grid-cols-2 gap-2">
                  {dbPlatforms.map((platform) => (
                    <div key={platform.id} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`platform-${platform.id}`} 
                        checked={formData.selectedPlatforms.includes(platform.id)}
                        onCheckedChange={() => togglePlatform(platform.id)}
                      />
                      <label 
                        htmlFor={`platform-${platform.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {platform.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {formData.selectedPlatforms.some(id => 
                dbPlatforms.find(p => p.id === id)?.name === "iOS") && (
                <div className="grid gap-2">
                  <label htmlFor="iosStoreUrl">App Store URL (iOS)</label>
                  <Input
                    id="iosStoreUrl"
                    type="url"
                    placeholder="https://apps.apple.com/app/..."
                    value={formData.iosStoreUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, iosStoreUrl: e.target.value }))}
                  />
                </div>
              )}

              {formData.selectedPlatforms.some(id => 
                dbPlatforms.find(p => p.id === id)?.name === "Android") && (
                <div className="grid gap-2">
                  <label htmlFor="androidStoreUrl">Google Play URL (Android)</label>
                  <Input
                    id="androidStoreUrl"
                    type="url"
                    placeholder="https://play.google.com/store/apps/details?id=..."
                    value={formData.androidStoreUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, androidStoreUrl: e.target.value }))}
                  />
                </div>
              )}

              <div className="grid gap-2">
                <label className="font-medium">Sports</label>
                <div className="grid grid-cols-2 gap-2">
                  {dbSports.map((sport) => (
                    <div key={sport.id} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`sport-${sport.id}`} 
                        checked={formData.selectedSports.includes(sport.id)}
                        onCheckedChange={() => toggleSport(sport.id)}
                      />
                      <label 
                        htmlFor={`sport-${sport.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {sport.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-between mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/my-listings')}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditApp;
